import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useCurrentClinicId } from "@/hooks/useAppointments";
import type {
  FinancialAccount,
  FinancialCashSession,
  FinancialCategory,
  FinancialEntry,
  FinancialTitle,
  PaymentMethod,
  TitleKind,
} from "@/lib/financial/types";
import { fetchAllPages } from "@/lib/financial/paginate";

// As tabelas financial_* ainda não estão no types.ts gerado; o cliente é usado sem tipagem
// aqui e os retornos são tipados pelos modelos de src/lib/financial/types.ts.
type Result<T> = Promise<{ data: T | null; error: { message: string } | null }>;
interface UntypedQuery<T> extends PromiseLike<{ data: T | null; error: { message: string } | null }> {
  select(cols?: string): UntypedQuery<T>;
  eq(col: string, v: unknown): UntypedQuery<T>;
  gte(col: string, v: unknown): UntypedQuery<T>;
  lte(col: string, v: unknown): UntypedQuery<T>;
  in(col: string, v: unknown[]): UntypedQuery<T>;
  order(col: string, opts?: { ascending?: boolean }): UntypedQuery<T>;
  range(from: number, to: number): UntypedQuery<T>;
  insert(v: unknown): UntypedQuery<T>;
  update(v: unknown): UntypedQuery<T>;
  single(): UntypedQuery<T>;
  maybeSingle(): UntypedQuery<T>;
}
const db = supabase as unknown as {
  from<T>(table: string): UntypedQuery<T>;
  rpc<T>(fn: string, args: Record<string, unknown>): Result<T>;
};

function unwrap<T>(res: { data: T | null; error: { message: string } | null }): T {
  if (res.error) throw new Error(res.error.message);
  return res.data as T;
}

export const financialKeys = {
  all: ["financial"] as const,
  setup: (clinicId: string) => ["financial", clinicId, "setup"] as const,
  titles: (clinicId: string, kind: TitleKind) => ["financial", clinicId, "titles", kind] as const,
  entries: (clinicId: string) => ["financial", clinicId, "entries"] as const,
  sessions: (clinicId: string) => ["financial", clinicId, "sessions"] as const,
};

function useClinic() {
  const { data: clinicId } = useCurrentClinicId();
  return clinicId ?? null;
}

/** Contas e categorias da clínica (cria os padrões na primeira visita). */
export function useFinancialSetup() {
  const clinicId = useClinic();
  return useQuery({
    queryKey: financialKeys.setup(clinicId ?? ""),
    enabled: !!clinicId,
    staleTime: 5 * 60 * 1000,
    queryFn: async () => {
      unwrap(await db.rpc<null>("financial_ensure_defaults", { p_clinic_id: clinicId }));
      const [accounts, categories] = await Promise.all([
        db.from<FinancialAccount[]>("financial_accounts").select("*").eq("clinic_id", clinicId).order("created_at"),
        db.from<FinancialCategory[]>("financial_categories").select("*").eq("clinic_id", clinicId).order("sort_order"),
      ]);
      return {
        clinicId: clinicId as string,
        accounts: unwrap(accounts) ?? [],
        categories: unwrap(categories) ?? [],
      };
    },
  });
}

export function useFinancialTitles(kind: TitleKind, enabled = true) {
  const clinicId = useClinic();
  return useQuery({
    queryKey: financialKeys.titles(clinicId ?? "", kind),
    enabled: !!clinicId && enabled,
    queryFn: () =>
      fetchAllPages(async (from, to) =>
        unwrap(
          await db
            .from<FinancialTitle[]>("financial_titles")
            .select("*")
            .eq("clinic_id", clinicId)
            .eq("kind", kind)
            .in("status", ["open", "paid"])
            .order("due_date")
            .order("id")
            .range(from, to),
        ) ?? [],
      ),
  });
}

/** O razão inteiro da clínica (anulados inclusos; o cálculo os descarta). */
export function useFinancialEntries(enabled = true) {
  const clinicId = useClinic();
  return useQuery({
    queryKey: financialKeys.entries(clinicId ?? ""),
    enabled: !!clinicId && enabled,
    queryFn: () =>
      fetchAllPages(async (from, to) =>
        unwrap(
          await db
            .from<FinancialEntry[]>("financial_entries")
            .select("*")
            .eq("clinic_id", clinicId)
            .order("occurred_at", { ascending: false })
            .order("id")
            .range(from, to),
        ) ?? [],
      ),
  });
}

export function useCashSessions(enabled = true) {
  const clinicId = useClinic();
  return useQuery({
    queryKey: financialKeys.sessions(clinicId ?? ""),
    enabled: !!clinicId && enabled,
    queryFn: () =>
      fetchAllPages(async (from, to) =>
        unwrap(
          await db
            .from<FinancialCashSession[]>("financial_cash_sessions")
            .select("*")
            .eq("clinic_id", clinicId)
            .order("business_date", { ascending: false })
            .range(from, to),
        ) ?? [],
      ),
  });
}

function useInvalidate() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: financialKeys.all });
}

// ── títulos ───────────────────────────────────────────────────────────────────
export interface TitleInput {
  kind: TitleKind;
  counterparty_name: string;
  counterparty_detail: string | null;
  description: string;
  category_id: string | null;
  payment_method: PaymentMethod;
  due_date: string;
  amount: number;
}

export function useCreateTitles() {
  const clinicId = useClinic();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ input, repeatMonths }: { input: TitleInput; repeatMonths: number }) => {
      const count = Math.max(1, repeatMonths);
      const rows = Array.from({ length: count }, (_, i) => {
        const due = new Date(`${input.due_date}T12:00:00`);
        due.setMonth(due.getMonth() + i);
        const iso = `${due.getFullYear()}-${String(due.getMonth() + 1).padStart(2, "0")}-${String(due.getDate()).padStart(2, "0")}`;
        return {
          ...input,
          clinic_id: clinicId,
          due_date: iso,
          competence_date: iso,
          installment_number: count > 1 ? i + 1 : null,
          installment_count: count > 1 ? count : null,
        };
      });
      unwrap(await db.from("financial_titles").insert(rows));
    },
    onSuccess: invalidate,
  });
}

export function useUpdateTitle() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<TitleInput> }) => {
      const next = patch.due_date ? { ...patch, competence_date: patch.due_date } : patch;
      unwrap(await db.from("financial_titles").update(next).eq("id", id));
    },
    onSuccess: invalidate,
  });
}

export function useCancelTitle() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      unwrap(
        await db
          .from("financial_titles")
          .update({ status: "canceled", canceled_at: new Date().toISOString(), cancel_reason: "deleted" })
          .eq("id", id),
      );
    },
    onSuccess: invalidate,
  });
}

export function useSettleTitle() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ titleId, accountId }: { titleId: string; accountId: string }) => {
      unwrap(await db.rpc("financial_settle_title", { p_title_id: titleId, p_account_id: accountId }));
    },
    onSuccess: invalidate,
  });
}

export function useUnsettleTitle() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (titleId: string) => {
      unwrap(await db.rpc("financial_unsettle_title", { p_title_id: titleId }));
    },
    onSuccess: invalidate,
  });
}

export function useRenegotiate() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: {
      titleIds: string[];
      installments: number;
      firstDue: string;
      paymentMethod: PaymentMethod;
      description: string;
    }) => {
      unwrap(
        await db.rpc("financial_renegotiate", {
          p_title_ids: args.titleIds,
          p_installments: args.installments,
          p_first_due: args.firstDue,
          p_payment_method: args.paymentMethod,
          p_description: args.description,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

// ── lançamentos ───────────────────────────────────────────────────────────────
export interface EntryInput {
  account_id: string;
  category_id: string | null;
  amount: number;
  description: string;
  detail?: string | null;
  payment_method?: PaymentMethod | null;
  occurred_at: string;
  competence_date: string;
  origin?: "manual" | "cashbox";
  cash_session_id?: string | null;
}

export function useCreateEntries() {
  const clinicId = useClinic();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (inputs: EntryInput[]) => {
      unwrap(
        await db.from("financial_entries").insert(
          inputs.map((i) => ({ origin: "manual", ...i, clinic_id: clinicId })),
        ),
      );
    },
    onSuccess: invalidate,
  });
}

export function useUpdateEntry() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, patch }: { id: string; patch: Partial<EntryInput> }) => {
      unwrap(await db.from("financial_entries").update(patch).eq("id", id));
    },
    onSuccess: invalidate,
  });
}

/** "Excluir" nunca apaga: anula o lançamento e guarda quem e quando. */
export function useVoidEntry() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (id: string) => {
      const { data } = await supabase.auth.getUser();
      unwrap(
        await db
          .from("financial_entries")
          .update({ voided_at: new Date().toISOString(), voided_by: data.user?.id ?? null, void_reason: "deleted" })
          .eq("id", id),
      );
    },
    onSuccess: invalidate,
  });
}

// ── caixa do dia ──────────────────────────────────────────────────────────────
export function useOpenCashSession() {
  const clinicId = useClinic();
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async (args: { businessDate: string; openingAmount: number; operatorName: string }) => {
      unwrap(
        await db.from("financial_cash_sessions").insert({
          clinic_id: clinicId,
          business_date: args.businessDate,
          opening_amount: args.openingAmount,
          opened_by_name: args.operatorName,
        }),
      );
    },
    onSuccess: invalidate,
  });
}

export function useToggleCashSession() {
  const invalidate = useInvalidate();
  return useMutation({
    mutationFn: async ({ id, close, operatorName }: { id: string; close: boolean; operatorName: string }) => {
      if (close) {
        // fechar passa pelo banco: quem abriu fecha, mesmo sem permissão de editar o caixa
        unwrap(await db.rpc("financial_close_cash_session", { p_session_id: id, p_operator_name: operatorName }));
        return;
      }
      unwrap(
        await db
          .from("financial_cash_sessions")
          .update({ closed_at: null, closed_by: null, closed_by_name: null })
          .eq("id", id),
      );
    },
    onSuccess: invalidate,
  });
}
